import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { Calendar, Users } from 'lucide-react'
import { translate } from '@/i18n/index'

import { useMemberActivitiesApi } from '../api/activities'
import BackPillButton from '../components/BackPillButton'
import { filterCancelledSessions } from '../../utils/activityFilters'

function formatDateTime(value, locale, timeZone, memberText) {
  if (!value) return memberText("portalPresentation.member_time_to_be_announced")
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}) }).format(new Date(value))
  } catch {
    return value
  }
}

export function buildActivitySessionItems({ sessions, activity, allowWaitlist }) {
  const visibleSessions = filterCancelledSessions(sessions)
  const dedupedSessions = []
  const seenSessions = new Map()

  visibleSessions.forEach((session) => {
    if (!session?.id) return
    const existing = seenSessions.get(session.id)
    if (!existing) {
      seenSessions.set(session.id, session)
      dedupedSessions.push(session)
      return
    }
    const existingActive =
      existing.member_booking_status === 'booked' || existing.member_booking_status === 'waitlisted'
    const nextActive =
      session.member_booking_status === 'booked' || session.member_booking_status === 'waitlisted'
    if (nextActive && !existingActive) {
      seenSessions.set(session.id, session)
      const index = dedupedSessions.findIndex((item) => item.id === session.id)
      if (index !== -1) dedupedSessions[index] = session
    }
  })

  return dedupedSessions.map((session) => {
    const capacity = session.capacity ?? activity?.default_capacity ?? null
    const bookedCount = session.booked_count || 0
    const isFull = capacity != null && bookedCount >= capacity
    const canBook = !isFull || allowWaitlist
    const statusLabel =
      session.member_booking_status === 'booked'
        ? 'Booked'
        : session.member_booking_status === 'waitlisted'
          ? 'Waitlisted'
          : null

    return {
      ...session,
      capacity,
      bookedCount,
      isFull,
      canBook,
      statusLabel,
    }
  })
}

export function getSessionActionLabel({ session, allowWaitlist, isSaving, text = translate }) {
  if (isSaving) return text("golf.starter.updating")
  const isBooked =
    session.member_booking_status === 'booked' || session.member_booking_status === 'waitlisted'
  if (isBooked) return text("portalPresentation.member_cancel")
  if (session.isFull && allowWaitlist) return text("portalPresentation.member_join_waitlist")
  if (session.isFull) return text("portalPresentation.member_full")
  return text("portalPresentation.member_join")
}

export default function MemberActivityDetailPage({ clubCode }) {
  const clubTimeZone = useMemberTimeZone()
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { branding } = useOutletContext() || {}
  const { activityId } = useParams()
  const {
    getActivity,
    listActivitySessions,
    createBooking,
    cancelBooking,
  } = useMemberActivitiesApi()
  const [activity, setActivity] = useState(null)
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [savingSessionId, setSavingSessionId] = useState(null)

  const allowWaitlist = activity?.allow_waitlist ?? true

  const loadData = async () => {
    setLoading(true)
    setError('')
    try {
      const [activityData, sessionData] = await Promise.all([
        getActivity(clubCode, activityId),
        listActivitySessions(clubCode, activityId),
      ])
      setActivity(activityData)
      setSessions(Array.isArray(sessionData) ? sessionData : [])
    } catch (err) {
      setError("portalPresentation.member_unable_to_load_activity")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [activityId, clubCode, getActivity, listActivitySessions])

  const handleBooking = async (session) => {
    setSavingSessionId(session.id)
    try {
      if (session.member_booking_status === 'booked' || session.member_booking_status === 'waitlisted') {
        await cancelBooking(clubCode, activityId, session.member_booking_id)
      } else {
        await createBooking(clubCode, activityId, session.id)
      }
      await loadData()
    } catch (err) {
      setError("portalPresentation.member_unable_to_update_booking")
    } finally {
      setSavingSessionId(null)
    }
  }

  const sessionItems = useMemo(
    () => buildActivitySessionItems({ sessions, activity, allowWaitlist }),
    [sessions, activity, allowWaitlist],
  )

  return (
    <div className="mp-panel mp-activity-detail">
      <div className="mp-panel__header mp-activity-detail__header">
        <div>
          <p className="mp-eyebrow">{memberText("portalPresentation.member_activities")}</p>
          <h4 className="mp-panel__title">{activity?.name || memberText("portalPresentation.member_activity")}</h4>
          {activity?.description ? (
            <p className="mp-panel-subtitle">{activity.description}</p>
          ) : (
            <p className="mp-panel-subtitle mp-text-muted">{memberText("portalPresentation.member_book_or_manage_your_reservation")}</p>
          )}
        </div>
        <BackPillButton to={`/${clubCode}/activities`}>{memberText("portalPresentation.member_back_to_activities")}</BackPillButton>
      </div>

      {loading ? (
        <div className="mp-panel__body mp-text-muted">{memberText("portalPresentation.member_loading_sessions")}</div>
      ) : error ? (
        <div className="mp-panel__body mp-text-muted">{memberText(error)}</div>
      ) : sessionItems.length === 0 ? (
        <div className="mp-empty mp-activity-detail__empty">{memberText("portalPresentation.member_no_sessions_are_scheduled_yet")}</div>
      ) : (
        <div className="mp-session-grid mp-activity-session-grid">
          {sessionItems.map((session) => {
            const statusBadge =
              session.member_booking_status === 'booked'
                ? { label: memberText("golf.canonical.attendance.booked"), variant: 'mp-status-badge--active' }
                : session.member_booking_status === 'waitlisted'
                  ? { label: memberText("portalPresentation.member_waitlisted"), variant: 'mp-status-badge--muted' }
                  : session.isFull && !allowWaitlist
                    ? { label: memberText("portalPresentation.member_full"), variant: 'mp-status-badge--muted' }
                    : { label: memberText("portalPresentation.member_available"), variant: 'mp-status-badge--muted' }

            const isBooked = session.member_booking_status === 'booked' || session.member_booking_status === 'waitlisted'
            const isActionDisabled =
              savingSessionId === session.id ||
              (!session.canBook && !session.member_booking_status)

            return (
              <div key={session.id} className="mp-session-card mp-activity-session-card">
                <div className="mp-session-card__header">
                  <p className="mp-session-card__title">{formatDateTime(session.starts_at, locale, clubTimeZone || branding?.timeZone, memberText)}</p>
                  {statusBadge ? (
                    <span className={`mp-status-badge ${statusBadge.variant}`}>{statusBadge.label}</span>
                  ) : null}
                </div>
                <div className="mp-session-card__meta">
                  <span className="mp-pill muted">
                    <Users size={14} />
                    {memberText("portalPresentation.member__count_booked", { count: session.bookedCount })} </span>
                  {session.capacity ? (
                    <span className="mp-pill muted">{memberText("portalPresentation.member_capacity_count", { count: session.capacity })}</span>
                  ) : null}
                </div>
                <div className="mp-session-card__actions">
                  <button
                    type="button"
                    className={`mp-btn mp-btn--sm ${isBooked ? 'mp-btn--outline' : 'mp-btn--primary'}`}
                    onClick={() => handleBooking(session)}
                    disabled={isActionDisabled}
                  >
                    <Calendar size={14} />
                    {getSessionActionLabel({
                      session,
                      allowWaitlist,
                      isSaving: savingSessionId === session.id,
                      text: memberText,
                    })}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
